import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import {
  Loader2,
  Send,
  ArrowLeft,
  User,
  MessageCircle,
  MoreVertical,
  Trash2,
  Ban,
  Check,
  CheckCheck,
  ShieldCheck,
  EyeOff,
  Inbox,
  Search,
  X,
  UserCheck,
  Plus,
  Smile,
  Reply
} from 'lucide-react';

// --- Constants & Helpers ---
const EMOJI_LIST = [
  '😀', '😂', '😍', '🤣', '😊', '😎', '🤔', '😮', '😢', '😡', '🤯', '🥳',
  '👍', '👎', '👌', '✌️', '🤝', '🙏', '👏', '💪', '🤞',
  '💰', '💸', '💵', '💴', '💶', '💷', '💎', '💳', '🤑', '💲', '🏦',
  '📈', '📉', '📊', '💹', '🔢', '🎯',
  '₿', '🪙', '🚀', '🌕', '🔐', '🔑', '⛓️', '🧱',
  '🔥', '❤️', '🎉', '💯', '⚠️', '✅', '❌', '🌟', '💡'
];

// ============================================================
// ✅ SECURITY HELPERS
// ============================================================

/**
 * 1. SANITIZE URLS
 * Prevents 'javascript:' protocol injection in href/src.
 * Only allows http, https, and relative paths.
 */
const sanitizeUrl = (url) => {
  if (!url) return null;
  
  // Allow relative paths (e.g., /uploads/image.jpg)
  if (url.startsWith('/')) return url;
  
  // Allow safe protocols
  if (/^https?:\/\//i.test(url)) return url;
  
  // Block everything else (javascript:, data:, vbscript:, etc.)
  return null;
};

/**
 * 2. VALIDATE FILE TYPE
 * Ensures uploaded files match allowed extensions.
 */
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'mp4', 'mov', 'webm'];
const isValidFileType = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  return ALLOWED_EXTENSIONS.includes(ext);
};

function useInterval(callback, delay) {
  const savedCallback = useRef();
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

// --- Sub-Components ---

const ConversationListItem = ({ conversation, onClick, isActive, isRequest }) => {
  const hasUnread = conversation.unread_count > 0;
  const isOnline = conversation.is_online || false;
  // ✅ SECURITY: Sanitize Avatar URL
  const safeAvatar = sanitizeUrl(conversation.avatar);

  return (
    <button
      onClick={() => onClick(conversation)}
      className={`w-full p-3 flex items-center gap-3 transition-all duration-200 border-b border-gray-100 dark:border-gray-800 ${
        isActive 
          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500' 
          : 'hover:bg-gray-50 dark:hover:bg-gray-800/30 border-l-4 border-l-transparent'
      }`}
    >
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg overflow-hidden shadow-md">
          {safeAvatar ? (
            <img src={safeAvatar} alt={conversation.name} className="w-full h-full object-cover" />
          ) : (
            conversation.name?.charAt(0).toUpperCase()
          )}
        </div>
        {isOnline && (
          <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white dark:border-gray-900 rounded-full shadow-sm" />
        )}
      </div>
      
      <div className="flex-1 text-left overflow-hidden">
        <div className="flex justify-between items-center mb-0.5">
          <span className={`truncate text-sm font-semibold ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
            {conversation.name}
          </span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
            {new Date(conversation.last_message_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
          </span>
        </div>
        <p className={`text-xs truncate ${hasUnread && !isRequest ? 'text-gray-800 dark:text-gray-200 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
          {isRequest ? "Sent a message request" : conversation.last_message}
        </p>
      </div>

      {hasUnread && !isRequest && (
        <div className="relative flex items-center justify-center">
           <div className="absolute w-6 h-6 bg-blue-400 rounded-full opacity-40 animate-ping" />
           <div className="relative w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-[10px] text-white font-bold shadow-md">
             {conversation.unread_count}
           </div>
        </div>
      )}
    </button>
  );
};

const BlockedUserItem = ({ user, onUnblock }) => {
  // ✅ SECURITY: Sanitize Avatar URL
  const safeAvatar = sanitizeUrl(user.avatar);
  
  return (
  <div className="w-full p-3 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800">
    <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white font-bold overflow-hidden">
      {safeAvatar ? (
        <img src={safeAvatar} alt={user.name} className="w-full h-full object-cover" />
      ) : (
        user.name?.charAt(0).toUpperCase()
      )}
    </div>
    <div className="flex-1 text-left">
      <span className="font-medium text-gray-800 dark:text-gray-200">{user.name}</span>
    </div>
    <button 
      onClick={() => onUnblock(user.id)}
      className="text-xs px-3 py-1 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400"
    >
      Unblock
    </button>
  </div>
)};

// --- UPDATED: MessageBubble with Scroll & Highlight Logic ---
const MessageBubble = ({ msg, isOwn, onReply, currentUserId, onScrollTo, isHighlighted }) => {
  const isImage = msg.message_type === 'image';
  const isVideo = msg.message_type === 'video';
  const hasReply = !!msg.reply_to;
  
  // ✅ SECURITY: Sanitize message content used as URLs
  const safeContent = sanitizeUrl(msg.content);
  // Text content inside {} is safe in React, no need to sanitize plain text messages

  return (
    <div 
      id={`message-${msg.id}`} 
      className={`flex w-full mb-3 group relative ${isOwn ? 'justify-end' : 'justify-start'}`}
    >
      {/* Reply Button (Visible on Hover) */}
      <button 
        onClick={() => onReply(msg)}
        className={`absolute top-1 ${isOwn ? 'left-1' : 'right-1'} p-1.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-gray-300 dark:hover:bg-gray-600 z-10 shadow-sm`}
      >
        <Reply size={14} />
      </button>

      <div className={`max-w-[85%] md:max-w-[70%] rounded-2xl shadow-sm overflow-hidden min-w-0 transition-all duration-300 ${
        isHighlighted 
          ? 'ring-2 ring-blue-400 ring-offset-2 dark:ring-offset-gray-950' 
          : ''
      } ${
        isOwn 
          ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-br-sm' 
          : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-sm border border-gray-100 dark:border-gray-700'
      }`}>
        
        {/* Quote Reply Header */}
        {hasReply && (
          <div 
            onClick={() => onScrollTo(msg.reply_to.id)}
            className={`px-3 py-1.5 mb-1 border-l-2 text-xs flex flex-col cursor-pointer hover:opacity-80 ${
              isOwn 
                ? 'bg-blue-600/30 border-blue-200 text-blue-100' 
                : 'bg-gray-100 dark:bg-gray-700 border-blue-500 text-gray-600 dark:text-gray-400'
            }`}
          >
            <span className="font-bold flex items-center gap-1">
              {msg.reply_to.sender_id === currentUserId ? 'You' : 'Replying'}
            </span>
            <span className="truncate opacity-90">{msg.reply_to.content}</span>
          </div>
        )}

        {isImage && safeContent && (
          <a href={safeContent} target="_blank" rel="noopener noreferrer">
            <img src={safeContent} alt="Sent image" className="w-full max-h-60 object-cover" />
          </a>
        )}
        {isVideo && safeContent && (
          <video controls className="w-full max-h-60 bg-black">
            <source src={safeContent} type="video/mp4" />
          </video>
        )}

        {(msg.message_type === 'text' || !msg.message_type) && (
          <div className="px-4 py-2">
            <p className="text-sm whitespace-pre-wrap break-all overflow-hidden">
              {msg.content}
            </p>
          </div>
        )}

        <div className={`px-3 pb-1.5 pt-1 flex items-center justify-end gap-1 ${isOwn ? 'text-blue-100' : 'text-gray-400'}`}>
          <span className="text-[9px] opacity-70">
              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          {isOwn && (
              msg.is_read 
              ? <CheckCheck size={12} className="opacity-90" title="Read" /> 
              : <Check size={12} className="opacity-50" title="Sent" />
          )}
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---

export default function MessagePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Data State
  const [activeTab, setActiveTab] = useState('chats');
  const [conversations, setConversations] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  
  // UI State
  const [loadingConvos, setLoadingConvos] = useState(true);
  const [loadingChat, setLoadingChat] = useState(false);
  const [sending, setSending] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false); 
  const [showEmojis, setShowEmojis] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showHeaderOptions, setShowHeaderOptions] = useState(false);
  
  // NEW: Reply & Highlight State
  const [replyingTo, setReplyingTo] = useState(null);
  const [highlightedMsgId, setHighlightedMsgId] = useState(null);

  // State for tab visibility (optimization)
  const [isTabVisible, setIsTabVisible] = useState(true);

  // Search State
  const [searchQuery, setSearchQuery] = useState('');

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null); // Ref for the scroll container
  const optionsRef = useRef(null);
  const headerOptionsRef = useRef(null);
  const fileInputRef = useRef(null);
  const initialLoadDone = useRef(false);

  // --- Core Logic ---

  const fetchConversations = useCallback(async (tab = activeTab) => {
    try {
      const [convRes, blockRes] = await Promise.all([
        api.get(`/messages/conversations`, { params: { tab } }).catch(() => ({ data: [] })),
        api.get('/messages/blocked').catch(() => ({ data: [] }))
      ]);
      setConversations(convRes.data);
      setBlockedUsers(blockRes.data);
    } catch (err) {
      console.error("Failed to fetch lists", err);
    } finally {
      setLoadingConvos(false);
    }
  }, [activeTab]);

  const fetchMessages = useCallback(async (partnerId) => {
    try {
      setLoadingChat(true);
      const res = await api.get(`/messages/conversation/${partnerId}`);
      setMessages(res.data);
      setIsBlocked(false);
    } catch (err) {
      if (err.response?.status === 403) setIsBlocked(true);
    } finally {
      setLoadingChat(false);
    }
  }, []);

  // Unified refresh function
  const refreshData = useCallback(async () => {
    await fetchConversations(activeTab);
    
    if (activeChat?.user_id) {
      try {
        const res = await api.get(`/messages/conversation/${activeChat.user_id}`);
        setMessages(res.data);
      } catch (err) {
        // Silent fail
      }
    }
  }, [activeTab, activeChat, fetchConversations]);

  // Visibility change listener
  useEffect(() => {
    const handleVisibilityChange = () => setIsTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Smart Polling
  useInterval(() => {
    if (isTabVisible) {
      refreshData();
    }
  }, isTabVisible ? 10000 : null);

  const containerRef = usePullToRefresh(refreshData);

  const openChat = useCallback(async (partner) => {
    const partnerId = partner.user_id || partner.id;

    setActiveChat({
      user_id: partnerId,
      name: partner.name,
      avatar: partner.avatar,
      is_blocked: partner.is_blocked || false,
      status: partner.status,
      is_online: partner.is_online || false
    });

    if (partnerId.toString() !== userId?.toString()) {
      navigate(`/dashboard/messages/${partnerId}`, { replace: true });
    }

    setSearchQuery(''); 
    setShowOptions(false);
    setReplyingTo(null);
    fetchMessages(partnerId);
    
  }, [navigate, fetchMessages, userId]);

  // Initial Load
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;
    
    const init = async () => {
      await fetchConversations();
      if (userId) {
        const existingConvo = conversations.find(c => c.user_id === parseInt(userId));
        if (existingConvo) {
            openChat(existingConvo);
        } else {
             try {
                const userRes = await api.get(`/messages/user/${userId}`);
                openChat({
                  user_id: userRes.data.id,
                  name: userRes.data.name,
                  avatar: userRes.data.avatar,
                  status: 'active',
                  is_online: false
                });
             } catch (err) {
                console.error("User not found");
             }
        }
      }
    };
    init();
  }, [userId]);

  useEffect(() => {
    fetchConversations(activeTab);
  }, [activeTab]);

  // Close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target)) setShowOptions(false);
      if (headerOptionsRef.current && !headerOptionsRef.current.contains(event.target)) setShowHeaderOptions(false);
      if (!event.target.closest('.emoji-picker')) setShowEmojis(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // UPDATED: Smart Auto-scroll Logic
  useEffect(() => {
    if (!messagesContainerRef.current || messages.length === 0) return;

    const container = messagesContainerRef.current;
    const lastMsg = messages[messages.length - 1];

    // Check if user is near bottom (within 150px)
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
    
    // Check if the last message is mine
    const isMyMessage = lastMsg.sender_id === currentUser?.id;

    // Only scroll if I sent the message OR if I am already near the bottom
    if (isMyMessage || isNearBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, currentUser?.id]);

  const totalUnread = conversations.reduce((acc, curr) => acc + (curr.unread_count || 0), 0);

  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    return conversations.filter(c => 
      c.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [conversations, searchQuery]);

  const hasRequests = activeTab === 'requests' && conversations.length > 0; 

  // --- Action Handlers ---

  const handleBack = () => {
    setActiveChat(null);
    navigate('/dashboard/messages');
  };

  // NEW: Scroll to message handler
  const handleScrollToMessage = (id) => {
    if (!id) return;
    const element = document.getElementById(`message-${id}`);
    if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Trigger highlight
        setHighlightedMsgId(id);
        
        // Remove highlight after 2 seconds
        setTimeout(() => {
            setHighlightedMsgId(null);
        }, 2000);
    }
  };

  const handleSend = async (e, type = 'text', content = newMessage) => {
    e?.preventDefault();
    if ((!content.trim() && type === 'text') || !activeChat || sending) return;

    setSending(true);
    try {
      const payload = { 
        receiver_id: activeChat.user_id, 
        content: content,
        message_type: type
      };

      if (replyingTo) {
        payload.reply_to_id = replyingTo.id;
      }

      await api.post('/messages/send', payload);
      
      setNewMessage('');
      setReplyingTo(null);
      setShowEmojis(false);
      fetchConversations(activeTab);
      fetchMessages(activeChat.user_id);
    } catch (err) {
      alert("Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // ✅ SECURITY: Validate File Extension
    if (!isValidFileType(file.name)) {
       alert("Invalid file type. Only images and videos are allowed.");
       if(fileInputRef.current) fileInputRef.current.value = "";
       return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert("File size should be less than 10MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const uploadRes = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fileUrl = uploadRes.data.url;
      const fileType = file.type.startsWith('video') ? 'video' : 'image';

      await handleSend(null, fileType, fileUrl);

    } catch (err) {
      console.error(err);
      alert("Failed to upload file");
    } finally {
      setUploading(false);
      if(fileInputRef.current) fileInputRef.current.value = ""; 
    }
  };

  const handleAcceptRequest = async () => {
    if(!activeChat) return;
    try {
        await api.post(`/messages/accept/${activeChat.user_id}`);
        setActiveTab('chats');
        fetchConversations('chats');
        setActiveChat(prev => ({...prev, status: 'active'}));
    } catch(err) {
        console.error(err);
    }
  };

  const handleHideChat = async () => {
    if(!window.confirm("Clear chat for you? The other user will still see it.")) return;
    try {
        await api.post(`/messages/hide/${activeChat.user_id}`);
        handleBack();
        fetchConversations();
    } catch(err) {
        alert("Failed to hide chat.");
    }
  };

  const handleClearChat = async () => {
    if(!window.confirm("Clear chat for everyone?")) return;
    try {
        await api.post(`/messages/clear/${activeChat.user_id}`);
        setMessages([]);
        fetchConversations(); 
    } catch(err) {
        console.error(err);
        alert("Failed to clear chat.");
    }
  };

  const handleBlockUser = async () => {
    if(!window.confirm(`Block ${activeChat.name}?`)) return;
    try {
        await api.post(`/messages/block/${activeChat.user_id}`);
        setIsBlocked(true);
        setShowOptions(false);
        fetchConversations();
    } catch(err) {
        console.error(err);
    }
  };

  const handleUnblockUser = async (targetId = null) => {
    const idToUnblock = targetId || activeChat?.user_id;
    if(!idToUnblock) return;

    try {
        await api.delete(`/messages/block/${idToUnblock}`);
        if(!targetId && activeChat) setIsBlocked(false);
        fetchConversations();
    } catch(err) {
        console.error(err);
    }
  };
  
  // ✅ SECURITY: Sanitize active chat avatar
  const safeActiveAvatar = sanitizeUrl(activeChat?.avatar);

  // --- Render ---

  return (
    <div 
      ref={containerRef}
      className="h-[calc(100dvh-80px)] flex bg-white dark:bg-gray-900 shadow-2xl overflow-hidden border-0 md:border border-gray-200 dark:border-gray-800 rounded-none md:rounded-xl m-0 md:m-4 relative"
    >
      
      {/* Sidebar */}
      <div className={`w-full md:w-80 lg:w-96 border-r border-gray-200 dark:border-gray-800 flex flex-col ${activeChat ? 'hidden md:flex' : 'flex'}`}>
        
        {/* Header */}
        <div className="p-6 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 border-b border-gray-100 dark:border-gray-800">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">Chats</h1>
            
            <div className="relative" ref={headerOptionsRef}>
              <button 
                onClick={() => setShowHeaderOptions(!showHeaderOptions)} 
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full focus:outline-none"
              >
                <MoreVertical size={20} className="text-gray-600 dark:text-gray-300" />
              </button>
              
              {showHeaderOptions && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl z-50 overflow-hidden border border-gray-100 dark:border-gray-700">
                  <button 
                    onClick={() => { setShowBlockedModal(true); setShowHeaderOptions(false); }} 
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-200"
                  >
                    <Ban size={16} /> Blocked Users
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative mb-4">
            <input 
              type="text" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users..." 
              className="w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
            />
            <Search className="absolute left-3 top-2.5 text-gray-400 dark:text-gray-500" size={16} />
          </div>

          {/* Tabs */}
          <div className="flex gap-2 relative z-10">
            <button 
              onClick={() => setActiveTab('chats')}
              className={`flex-1 py-1.5 text-xs font-semibold rounded-full transition-all duration-300 relative ${
                activeTab === 'chats' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Chats
              {totalUnread > 0 && activeTab !== 'chats' && (
                 <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] text-white font-bold">
                    {totalUnread > 9 ? '9+' : totalUnread}
                 </span>
              )}
            </button>
            <button 
              onClick={() => setActiveTab('requests')}
              className={`flex-1 py-1.5 text-xs font-semibold rounded-full transition-all duration-300 relative ${
                activeTab === 'requests' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Requests
              {hasRequests && activeTab !== 'requests' && (
                 <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                 </span>
              )}
            </button>
          </div>
        </div>

        {/* List Area */}
        <div className="flex-1 overflow-y-auto">
          {loadingConvos ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-blue-500" /></div>
          ) : (
            <>
              {filteredConversations.length === 0 ? (
                <div className="text-center text-gray-400 dark:text-gray-500 py-12 px-4">
                  <Inbox size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {searchQuery ? "No matches found." : (activeTab === 'requests' ? "No new requests." : "No conversations yet.")}
                  </p>
                </div>
              ) : (
                filteredConversations.map(c => (
                  <ConversationListItem 
                    key={c.user_id} 
                    conversation={c} 
                    onClick={openChat}
                    isActive={activeChat?.user_id === c.user_id}
                    isRequest={activeTab === 'requests'}
                  />
                ))
              )}
            </>
          )}
        </div>
      </div>

      {/* Chat View */}
      <div className={`flex-1 flex flex-col bg-white dark:bg-gray-900 ${!activeChat ? 'hidden md:flex' : 'flex'}`}>
        {activeChat ? (
          <>
            {/* Header */}
            <div className="min-h-[70px] border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-4 bg-gray-50 dark:bg-gray-800/50 z-10">
              <div className="flex items-center gap-3 min-w-0">
                <button 
                  onClick={handleBack} 
                  className="md:hidden p-2 -ml-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full text-gray-700 dark:text-gray-300 shrink-0"
                >
                  <ArrowLeft size={20} />
                </button>
                
                <div 
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded-lg transition-colors min-w-0"
                  onClick={() => navigate(`/dashboard/profile/${activeChat.user_id}`)}
                >
                  <div className="relative shrink-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold overflow-hidden shadow">
                      {safeActiveAvatar ? (
                        <img src={safeActiveAvatar} alt={activeChat.name} className="w-full h-full object-cover" />
                      ) : (
                        activeChat.name?.charAt(0).toUpperCase()
                      )}
                    </div>
                    {activeChat.is_online && (
                       <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{activeChat.name}</h3>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {activeChat.status === 'request' ? "Message Request" : activeChat.is_online ? <span className="text-green-500 font-bold">Online</span> : "Tap for info"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Side Actions */}
              <div className="flex items-center gap-2 shrink-0">
                  {activeChat.status === 'request' ? (
                      <button 
                        onClick={handleAcceptRequest}
                        className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold rounded-full hover:opacity-90 flex items-center gap-2 shadow-md"
                      >
                        <UserCheck size={14} /> Accept
                      </button>
                  ) : (
                      <div className="relative" ref={optionsRef}>
                        <button onClick={() => setShowOptions(!showOptions)} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full">
                          <MoreVertical size={20} className="text-gray-600 dark:text-gray-300" />
                        </button>
                        
                        {showOptions && (
                            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border dark:border-gray-700 z-50 overflow-hidden">
                                <div className="py-1">
                                  <button onClick={handleHideChat} className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-300">
                                      <EyeOff size={16} /> Clear Chat for Me
                                  </button>
                                  
                                  <button onClick={handleClearChat} className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 text-red-500">
                                      <Trash2 size={16} /> Clear Chat for All
                                  </button>

                                  <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>

                                  {isBlocked ? (
                                     <button onClick={() => handleUnblockUser()} className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 text-green-600">
                                        <ShieldCheck size={16} /> Unblock User
                                     </button>
                                  ) : (
                                     <button onClick={handleBlockUser} className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 text-red-500">
                                        <Ban size={16} /> Block User
                                     </button>
                                  )}
                                </div>
                            </div>
                        )}
                      </div>
                  )}
              </div>
            </div>

            {/* Messages Area - Added Ref for Smart Scroll */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-1 bg-slate-50 dark:bg-gray-950/50">
              {loadingChat ? (
                <div className="flex justify-center items-center h-full"><Loader2 className="animate-spin text-blue-500" /></div>
              ) : isBlocked ? (
                 <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 p-8">
                    <Ban size={48} className="mb-4 opacity-50" />
                    <p className="font-medium">You blocked this user</p>
                    <button onClick={() => handleUnblockUser()} className="mt-4 text-xs text-blue-500 underline">Unblock</button>
                 </div>
              ) : (
                <>
                  {activeChat.status === 'request' ? (
                     <div className="flex flex-col items-center justify-center h-full text-center p-8">
                        <UserCheck size={48} className="text-blue-400 mb-4" />
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Message Request</h3>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                            {activeChat.name} wants to send you a message. Accept to view and reply.
                        </p>
                        {messages.length > 0 && (
                           <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg text-sm italic text-gray-600 dark:text-gray-300 max-w-xs mt-4 opacity-70 blur-sm hover:blur-none transition-all">
                              "{messages[0].content}"
                           </div>
                        )}
                     </div>
                  ) : (
                    <>
                      {messages.length === 0 && (
                         <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 p-8">
                            <MessageCircle size={48} className="mb-4 opacity-50" />
                            <p className="font-medium">No messages yet</p>
                            <p className="text-xs">Say hi to {activeChat.name}!</p>
                         </div>
                      )}
                      {messages.map(msg => (
                        <MessageBubble 
                          key={msg.id} 
                          msg={msg} 
                          isOwn={msg.sender_id === currentUser?.id} 
                          onReply={setReplyingTo}
                          currentUserId={currentUser?.id}
                          onScrollTo={handleScrollToMessage}
                          isHighlighted={highlightedMsgId === msg.id}
                        />
                      ))}
                    </>
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            {activeChat.status !== 'request' && !isBlocked && (
              <div className="border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 relative">
                
                {/* Reply Preview Bar */}
                {replyingTo && (
                  <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20">
                    <div className="flex-1 overflow-hidden border-l-4 border-blue-500 pl-2">
                      <span className="text-xs font-bold text-blue-600 dark:text-blue-400 block">
                        Replying to {replyingTo.sender_id === currentUser?.id ? 'yourself' : activeChat?.name}
                      </span>
                      <p className="text-xs text-gray-600 dark:text-gray-400 truncate mt-0.5">
                        {replyingTo.content}
                      </p>
                    </div>
                    <button 
                      onClick={() => setReplyingTo(null)} 
                      className="ml-2 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full text-gray-500"
                    >
                      <X size={18} />
                    </button>
                  </div>
                )}

                <div className="p-3">
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    onChange={handleFileUpload} 
                    className="hidden" 
                    accept="image/*,video/*" 
                  />

                  {showEmojis && (
                    <div className="absolute bottom-16 left-4 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl shadow-xl p-3 grid grid-cols-6 gap-2 z-50 emoji-picker max-w-[calc(100vw-2rem)]">
                      {EMOJI_LIST.map(emoji => (
                        <button 
                          key={emoji} 
                          onClick={() => {
                            setNewMessage(prev => prev + emoji);
                          }}
                          className="text-xl sm:text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 transition-transform hover:scale-125"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}

                  <form onSubmit={handleSend} className="flex items-end gap-2">
                    <button 
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="p-2 text-gray-500 hover:text-blue-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors disabled:opacity-50"
                    >
                      {uploading ? <Loader2 size={20} className="animate-spin" /> : <Plus size={20} />}
                    </button>

                    {/* FIXED: Mobile Zoom Issue - Changed text-sm to text-base (16px) */}
                    <div className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl px-4 py-2 flex items-center gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={replyingTo ? "Write a reply..." : "Type a message..."}
                        className="flex-1 bg-transparent text-gray-900 dark:text-white text-base focus:outline-none"
                      />
                      <button 
                        type="button" 
                        onClick={() => setShowEmojis(!showEmojis)}
                        className="text-gray-400 hover:text-yellow-500 transition-colors"
                      >
                        <Smile size={20} />
                      </button>
                    </div>

                    <button 
                      type="submit" 
                      disabled={!newMessage.trim() || sending}
                      className="p-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full hover:shadow-lg hover:shadow-blue-500/30 transition-all disabled:opacity-50 disabled:shadow-none"
                    >
                      {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex-col items-center justify-center text-gray-400 dark:text-gray-500 hidden md:flex">
            <MessageCircle size={64} className="mb-4 opacity-20" />
            <p className="font-medium text-lg">Select a message</p>
            <p className="text-sm">Choose from your existing conversations.</p>
          </div>
        )}
      </div>

      {/* Blocked Users Modal */}
      {showBlockedModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
              <h3 className="font-bold text-lg text-gray-900 dark:text-white">Blocked Users</h3>
              <button onClick={() => setShowBlockedModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-500 dark:text-gray-400">
                <X size={20} />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {blockedUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">No blocked users.</div>
              ) : (
                blockedUsers.map(u => (
                  <BlockedUserItem key={u.id} user={u} onUnblock={(id) => { handleUnblockUser(id); fetchConversations(); }} />
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}